import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { cloneDeep } from 'lodash';
import { OrganizationUnit } from 'src/app/misc/interface/organization-unit';
import { DataService } from 'src/app/services/data.service';
import { TranslatorPipe } from 'src/app/services/translator.pipe';
import { MessageService } from 'primeng/api';
import { PermissionRequestFormComponent } from '../permission-request-form/permission-request-form.component';
import { breadcrumb } from '../../admin/layout/header/header.component';

declare const window: any;

type PermissionChoice = 'ASSESSMENT' | 'SURVEY';

@Component({
  selector: 'app-permission-request-flow',
  templateUrl: './permission-request-flow.component.html',
  styleUrls: ['./permission-request-flow.component.scss']
})
export class PermissionRequestFlowComponent implements OnInit {
  @ViewChild(PermissionRequestFormComponent) permissionFormComponent?: PermissionRequestFormComponent;

  step = 1;
  selectedPermission: PermissionChoice | null = null;
  permissionOptions = [
    {
      value: 'ASSESSMENT' as PermissionChoice,
      title: 'Assessments',
      accent: 'assessment-accent',
      icon: 'pi pi-chart-line'
    },
    {
      value: 'SURVEY' as PermissionChoice,
      title: 'Surveys',
      accent: 'survey-accent',
      icon: 'pi pi-bolt'
    }
  ];

  signupForm!: FormGroup;
  permissionContactPrefill: any = null;

  city: Array<{ value: string; label: string; icon: string }> = [];
  ous: Array<OrganizationUnit> = [];
  ouBackup: Array<OrganizationUnit> = [];
  filtredOus: Array<OrganizationUnit> = [];
  selectedOus: Array<OrganizationUnit> = [];
  selectedOusIds: string[] = [];
  selectionName = 'الجهة التنظيمية';
  allowedTags = ['Ministries/Entities', 'Organisational Units', 'Organisational Structure', 'Branches'];

  captchaNotLoaded = false;
  loading = true;
  requestInProgress = false;
  successState = false;
  statusMessage = '';

  breadcrumbs: breadcrumb[] = []

  private translator = new TranslatorPipe();

  constructor(
    private fb: FormBuilder,
    private dataService: DataService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.initializeSignupForm();
    Promise.all([this.getCity(), this.getOUs()]).finally(() => {
      this.loading = false;
    });
    this.breadcrumbs = [
      {
        name: this.translate('Request permission'),
        link: '/permission-requests-flow',
        visible: true
      }
    ];
  }

  get selectedPermissionOption() {
    if (!this.selectedPermission) {
      return null;
    }
    return {
      value: this.selectedPermission,
      name: this.translate(this.selectedPermission)
    };
  }

  get canSubmit(): boolean {
    return this.step === 3 && !this.requestInProgress;
  }

  translate(key: string) {
    return this.translator.transform(key);
  }

  selectPermission(option: PermissionChoice) {
    this.selectedPermission = option;
    this.signupForm.get('permissionRequests')?.setValue([option]);
  }

  goToSignupStep(): void {
    if (!this.selectedPermission) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate('Please select'),
        detail: this.translate('Choose a permission to continue')
      });
      return;
    }
    this.step = 2;
  }

  goBack(): void {
    if (this.step > 1) {
      this.step -= 1;
    }
  }

  proceedToRequestDetails(): void {
    if (!this.ensureSignupSectionValid()) {
      return;
    }
    this.permissionContactPrefill = this.buildContactPrefill();
    this.step = 3;
    this.cdr.detectChanges();
  }

  private ensureSignupSectionValid(): boolean {
    this.markSignupFormTouched();
    if (this.selectedOusIds.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate('Organizational unit'),
        detail: this.translate('Please select at least one organizational unit')
      });
      return false;
    }
    if (this.signupForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Incomplete form'),
        detail: this.translate('Please complete all required signup fields')
      });
      return false;
    }
    return true;
  }

  async submitFlow(): Promise<void> {
    if (this.requestInProgress) {
      return;
    }
    if (!this.selectedPermission) {
      this.step = 1;
      this.messageService.add({
        severity: 'warn',
        summary: this.translate('Missing selection'),
        detail: this.translate('Please choose a permission type')
      });
      return;
    }
    if (!this.ensureSignupSectionValid()) {
      this.step = 2;
      return;
    }

    if (!this.permissionFormComponent) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Form not ready'),
        detail: this.translate('Permission form is still loading. Please wait.')
      });
      return;
    }

    const permissionValidation = this.permissionFormComponent.validateAndGetPayload();
    if (!permissionValidation.valid || !permissionValidation.payload) {
      this.step = 3;
      return;
    }

    this.requestInProgress = true;
    this.statusMessage = this.translate('Creating user and registering permission request...');

    try {
      const signupPayload = this.buildSignupPayload();
      const signupResponse = await this.dataService.postData('user/register', signupPayload);
      if (signupResponse.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Signup failed'),
          detail: signupResponse.msg || this.translate('Unable to create user account')
        });
        this.requestInProgress = false;
        return;
      }

      const permissionPayload = { ...permissionValidation.payload };
      const permissionResponse = await this.dataService.postData('permission-request/', permissionPayload, 'POST');
      if (permissionResponse.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Request failed'),
          detail: permissionResponse.message || this.translate('Unable to create permission request')
        });
        this.requestInProgress = false;
        return;
      }

      this.successState = true;
      this.statusMessage = this.translate('Permission request created successfully. Our team will review it shortly.');
      this.messageService.add({
        severity: 'success',
        summary: this.translate('Success'),
        detail: this.translate('User and permission request created successfully')
      });
    } catch (error) {
      console.error('Error submitting consolidated request', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Unexpected error'),
        detail: this.translate('Something went wrong while processing your request')
      });
    } finally {
      this.requestInProgress = false;
    }
  }

  private buildSignupPayload() {
    const formValue = this.signupForm.getRawValue();
    return {
      name: {
        first: formValue.fName,
        middle: formValue.mName,
        last: formValue.lName,
      },
      email: formValue.email?.trim(),
      phone: formValue.phone,
      national_id: formValue.national_id,
      gender: formValue.gender?.label,
      location: formValue.location,
      ou: this.selectedOusIds,
      active: {
        status: false,
        reason: 'NEW ACCOUNT',
        activationDate: null,
      },
      permissionRequests: formValue.permissionRequests || [],
    };
  }

  private buildContactPrefill() {
    const formValue = this.signupForm.value;
    const nameParts = [formValue.fName, formValue.mName, formValue.lName].filter(Boolean);
    const fullName = nameParts.join(' ').trim();
    return {
      contactOfficer: {
        employeeName: fullName,
        email: formValue.email,
        mobileNumber: formValue.phone
      },
      questionMaker: {
        employeeName: fullName,
        email: formValue.email,
        mobileNumber: formValue.phone
      }
    };
  }

  private initializeSignupForm(): void {
    this.signupForm = this.fb.group({
      recaptcha: ['', Validators.required],
      fName: [null, [Validators.required, Validators.minLength(3)]],
      mName: [null],
      lName: [null, [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      phone: [null, [
        Validators.required,
        Validators.pattern('^[+]?[0]?[0]?[(]?[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4,6}$')
      ]],
      national_id: [null, [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      gender: [null, [Validators.required]],
      location: [null, [Validators.required]],
      permissionRequests: [[]]
    });
  }

  private async getCity() {
    try {
      const res = await this.dataService.getData('location');
      if (!res.error) {
        this.city = res.data.map((location: any) => ({
          value: location._id,
          label: location.name,
          icon: 'pi pi-map-marker'
        }));
      }
    } catch (error) {
      console.error('Error loading locations', error);
    }
  }

  private async getOUs() {
    try {
      const res = await this.dataService.getData('ou');
      if (!res.error) {
        this.ous = res.data
          .filter((item: any) => this.allowedTags.includes(item?.type?.tag) && item.active === true);
        this.ouBackup = cloneDeep(this.ous);
        this.filtredOus = this.ous.filter((ou: any) => ou.parent == null);
      }
    } catch (error) {
      console.error('Error loading OUs', error);
    }
  }

  handleOU(id: any, onInitFlag = false) {
    if (!id || this.selectedOusIds.includes(id)) {
      return;
    }

    const selectedOU = this.ous.find((ou: any) => ou._id === id);
    if (!selectedOU) {
      return;
    }

    this.selectedOusIds.push(id);
    this.selectedOus.push(selectedOU);

    this.filtredOus = this.ous.filter(
      (ou: any) =>
        ou.parent?._id === id &&
        (ou.location?._id === this.signupForm.get('location')?.value || ou.location?._id === '64647832ff8dc886e5d30cd6')
    );

    const uniqueCategoryNames = [...new Set(this.filtredOus.map((obj: any) => obj.type.name))];
    this.selectionName = uniqueCategoryNames.join('/ ');

    if (!onInitFlag) {
      this.cdr.detectChanges();
    }
  }

  deleteOU(index: number) {
    this.selectedOus.splice(index);
    this.selectedOusIds.splice(index);

    if (this.selectedOus.length === 0) {
      this.filtredOus = this.ous.filter((ou: any) => ou.parent == null);
      this.selectionName = 'الجهة التنظيمية';
    } else {
      this.filtredOus = this.ous.filter(
        (ou: any) =>
          ou.parent?._id === this.selectedOus[this.selectedOus.length - 1]._id &&
          (ou.location?._id === this.signupForm.get('location')?.value || ou.location?._id === '64647832ff8dc886e5d30cd6')
      );
      const uniqueCategoryNames = [...new Set(this.filtredOus.map((obj: any) => obj.type.name))];
      this.selectionName = uniqueCategoryNames.join('/ ');
    }
  }

  changeCityFilter() {
    this.filtredOus = this.ous.filter((ou: any) => ou.parent == null);
    this.selectedOus = [];
    this.selectedOusIds = [];
    this.selectionName = 'الجهة التنظيمية';
  }

  reloadCaptcha() {
    if (window['grecaptcha']) {
      window['grecaptcha'].reset();
    }
  }

  goToHome() {
    this.router.navigate(['/home']);
  }

  private markSignupFormTouched(): void {
    Object.keys(this.signupForm.controls).forEach(key => {
      const control = this.signupForm.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });
  }
}

